"use server";

import { headers } from "next/headers";
import { db } from "@/lib/db";
import { rateLimit } from "@/lib/rate-limit";
import { validateLoginRequest } from "@/lib/login-request";
import { mailRecipients, sendMail } from "@/lib/mailer";

export type RequestState = { ok?: boolean; error?: string; phone?: string };

/**
 * A shop owner asking for a login. Writes a LoginRequest, emails the office,
 * and creates NO dealer — staff create the dealer in Notion and the next
 * refresh brings it in.
 */
export async function submitLoginRequest(_prev: RequestState, form: FormData): Promise<RequestState> {
  const v = validateLoginRequest({
    businessName: String(form.get("businessName") ?? ""),
    ownerName: String(form.get("ownerName") ?? ""),
    phone: String(form.get("phone") ?? ""),
    city: String(form.get("city") ?? ""),
    state: String(form.get("state") ?? ""),
    gstin: String(form.get("gstin") ?? ""),
    heardFrom: String(form.get("heardFrom") ?? ""),
    note: String(form.get("note") ?? ""),
    website: String(form.get("website") ?? ""),
  });
  if (!v.ok) return { error: v.error };

  const h = await headers();
  const ip = (h.get("x-forwarded-for") ?? h.get("x-real-ip") ?? "").split(",")[0].trim() || null;
  if (ip && !rateLimit(`login-request:ip:${ip}`, 5, 60 * 60 * 1000).ok) {
    return { error: "Too many requests from this connection. Please try again in an hour." };
  }
  const today = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const sameNumber = await db.loginRequest.count({ where: { phone: v.value.phone, createdAt: { gt: today } } });
  if (sameNumber >= 3) return { error: "We already have your request — Sportsun will call you." };

  const req = await db.loginRequest.create({ data: { ...v.value, ip } });

  const existing = await db.dealer.findFirst({ where: { phone: v.value.phone, active: true }, select: { code: true, businessName: true } });
  const text = [
    `New login request on the Sportsun dealer portal.`,
    ``,
    `Business: ${v.value.businessName}`,
    `Owner: ${v.value.ownerName}`,
    `Mobile: +91 ${v.value.phone}`,
    `City / State: ${v.value.city}, ${v.value.state}`,
    `GSTIN: ${v.value.gstin ?? "not given"}`,
    `Heard about Sportsun via: ${v.value.heardFrom ?? "not said"}`,
    `Note: ${v.value.note ?? "—"}`,
    existing ? `\nThis mobile already belongs to dealer ${existing.businessName} (${existing.code}).` : "",
    ``,
    `Review it at ${process.env.PUBLIC_URL ?? "https://sportsunorders.goatedd.tech"}/ops/requests`,
  ].join("\n");
  const mail = await sendMail({ to: mailRecipients(), subject: `Login request: ${v.value.businessName}, ${v.value.city}`, text });
  await db.loginRequest.update({
    where: { id: req.id },
    data: mail.ok ? { emailSentAt: new Date() } : { emailError: mail.error },
  });

  return { ok: true, phone: v.value.phone };
}
